import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingPaintersWCPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Painters Western Cape - Professional Painting Contractors | Residential & Commercial Services',
    description: 'Leading painting contractors serving the entire Western Cape region. Expert residential, commercial, and industrial painting services with over 18 years of experience. Quality workmanship, competitive rates, and customer satisfaction guaranteed.',
    keywords: 'painters Western Cape, painting contractors, house painters WC, commercial painting, residential painting, Cape Town painters, Stellenbosch painters, Paarl painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Western Cape',
          description: 'Professional painting contractors serving the Western Cape',
          address: 'Western Cape, South Africa',
          priceRange: '$$',
          serviceArea: ['Cape Town', 'Stellenbosch', 'Paarl', 'Somerset West', 'Hermanus', 'George', 'Mossel Bay']
        }),
        generateServiceSchema({
          name: 'Complete Painting Services',
          description: 'Residential, commercial, and industrial painting throughout Western Cape',
          provider: 'Painters Western Cape',
          areaServed: ['Western Cape', 'Cape Town', 'Garden Route'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Industrial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Western Cape",
    location: "Western Cape, South Africa",
    website: "https://painterswc.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/636e9be37f0130a4a7b3c4944783da61.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=stunning%20painted%20cape%20dutch%20style%20home%20exterior%20with%20gables%20white%20walls%20dark%20trim%20professional%20finish%20vineyard%20setting%20mountain%20views%20blue%20sky&width=800&height=600&seq=gallery-pwc1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20winery%20building%20painting%20project%20western%20cape%20modern%20facility%20professional%20painters%20working%20contemporary%20architecture%20wine%20country&width=800&height=600&seq=gallery-pwc2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20room%20painting%20western%20cape%20home%20sophisticated%20earth%20tones%20modern%20furniture%20natural%20light%20professional%20finish&width=800&height=600&seq=gallery-pwc3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=coastal%20property%20exterior%20painting%20western%20cape%20ocean%20views%20professional%20application%20weather%20resistant%20coating%20residential%20building%20seaside%20setting&width=800&height=600&seq=gallery-pwc4&orientation=landscape"
    ],
    services: [
      {
        title: "Residential Painting Excellence",
        description: "Comprehensive residential painting services for homes throughout the Western Cape. From Cape Town to the Garden Route, we transform houses, apartments, estates, and holiday homes with exceptional craftsmanship. Our team understands the diverse architectural styles found across the region, from historic Cape Dutch homesteads to modern coastal villas and everything in between.",
        features: ["Complete interior painting services", "Exterior facade and wall painting", "Cape Dutch heritage restoration", "Modern home painting", "Holiday home maintenance", "Luxury estate painting", "Color design consultation", "Premium paint selection", "Wallpaper installation and removal", "Wood staining and varnishing", "Ceiling and trim painting", "Multi-property management"]
      },
      {
        title: "Commercial & Industrial Painting",
        description: "Professional painting solutions for businesses, commercial properties, and industrial facilities across the Western Cape. We serve offices, retail spaces, wineries, hotels, restaurants, warehouses, and manufacturing facilities. Our experienced team manages projects of all sizes with minimal disruption to your operations, delivering quality results on time and within budget.",
        features: ["Office buildings and complexes", "Retail stores and shopping centers", "Wineries and tasting rooms", "Hotels and guest houses", "Restaurants and hospitality", "Industrial warehouses", "Manufacturing facilities", "Agricultural buildings", "Strata and body corporate", "High-rise structures", "After-hours scheduling", "Safety compliance certified"]
      },
      {
        title: "Specialized Coating Services",
        description: "Advanced painting and coating solutions designed for the Western Cape's diverse climate conditions. From coastal salt air to inland heat, we provide specialized treatments that protect and enhance your property. Our expertise includes roof painting, waterproofing, damp treatment, and protective coatings for various surfaces and environments.",
        features: ["Roof painting and restoration", "Waterproofing systems", "Damp proofing treatments", "Heat-reflective coatings", "Anti-corrosion protection", "Concrete sealing", "Epoxy floor coatings", "Texture coating applications", "Graffiti removal", "Protective industrial coatings", "Weather-resistant finishes", "UV protection treatments"]
      }
    ],
    serviceAreas: ["Cape Town", "Stellenbosch", "Paarl", "Somerset West", "Strand", "Hermanus", "George", "Mossel Bay", "Knysna", "Plettenberg Bay", "Franschhoek", "Wellington", "Worcester", "Swellendam", "Caledon", "Bredasdorp"],
    about: "Painters Western Cape is the premier painting contractor serving the entire Western Cape region of South Africa. With over 18 years of industry experience and a team of highly skilled, certified professionals, we have successfully completed thousands of painting projects across residential, commercial, and industrial sectors. Our reputation throughout the Western Cape is built on exceptional quality, reliability, competitive pricing, and unwavering dedication to customer satisfaction.",
    aboutExtended: [
      "Established in 2005, our company has grown to become one of the most trusted and respected painting contractors in the Western Cape. We understand that this diverse region presents unique challenges – from the coastal conditions of Cape Town and the Garden Route to the wine country climate of Stellenbosch and Paarl, from historic Cape Dutch architecture to contemporary commercial developments. Our extensive experience across all these environments ensures we deliver optimal results regardless of location or project type.",
      "Our team consists of fully qualified painters who undergo rigorous training in advanced painting techniques, safety procedures, and product applications. We maintain the highest professional standards and stay current with industry innovations through continuous education and certification programs. Every team member is committed to delivering exceptional craftsmanship and customer service on every project, whether it's a single-room refresh or a large-scale commercial development.",
      "We exclusively use premium quality paints and materials from leading manufacturers including Dulux, Plascon, Prominent Paints, and specialized coating brands. Each product is carefully selected based on the specific requirements of your project, considering factors such as climate exposure, surface type, desired finish, and longevity expectations. Our relationships with major suppliers ensure we have access to the latest products and technologies while maintaining competitive pricing for our clients.",
      "What truly sets Painters Western Cape apart is our comprehensive approach to project management and customer care. We believe successful painting projects require more than technical expertise – they demand clear communication, detailed planning, meticulous execution, and respect for your property and schedule. From initial consultation through final completion, we maintain transparent communication, provide detailed written quotations, adhere to agreed timelines, and ensure your complete satisfaction. Our project managers coordinate all aspects of the work, keeping you informed and addressing any concerns promptly and professionally.",
      "We are fully licensed, insured, and compliant with all relevant safety, environmental, and industry regulations throughout the Western Cape. Our comprehensive insurance coverage protects both our team and your property during all phases of work. We maintain strict safety protocols on every job site, use environmentally responsible practices, and ensure proper disposal of all materials in accordance with local regulations. Our commitment to professionalism is evident in every aspect of our operations, from our uniformed crews to our clean work practices and thorough post-project cleanup."
    ],
    ourApproach: [
      "Comprehensive on-site consultation and assessment",
      "Detailed project planning and scheduling",
      "Professional color consultation and design advice",
      "Thorough surface preparation and repairs",
      "Premium quality materials selection",
      "Expert application techniques",
      "Strict quality control procedures",
      "Clean and organized work practices",
      "Regular progress updates",
      "Complete cleanup and waste removal",
      "Final inspection and walkthrough",
      "Comprehensive warranty coverage",
      "After-service support"
    ],
    whyChoose: [
      "18+ years serving Western Cape",
      "Thousands of completed projects",
      "Fully licensed and insured",
      "Certified professional team",
      "Premium quality materials",
      "Competitive transparent pricing",
      "Free detailed quotations",
      "Comprehensive warranties",
      "Excellent customer reviews",
      "All Western Cape areas covered",
      "Residential and commercial expertise",
      "Heritage restoration specialists",
      "Environmentally responsible",
      "Safety compliance guaranteed",
      "Project management excellence",
      "Multi-property experience"
    ],
    testimonials: [
      {
        name: "Michael Botha",
        rating: 5,
        text: "Painters Western Cape did an exceptional job on our wine estate in Stellenbosch. The team was professional, efficient, and the quality of work is outstanding. They understood the heritage aspects of our property and delivered a perfect finish. Highly recommend!"
      },
      {
        name: "Catherine Williams",
        rating: 5,
        text: "We've used Painters Western Cape for multiple properties across the region and they consistently deliver excellent results. From our Cape Town office to our Hermanus holiday home, the quality and service are always top-notch. Great value and very reliable!"
      },
      {
        name: "Johan Pretorius",
        rating: 5,
        text: "Outstanding service from start to finish! They painted our commercial building in George and managed the entire project flawlessly. Professional team, quality workmanship, and completed on schedule. Will definitely use them for future projects!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Western Cape</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Western Cape</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We provide professional painting services throughout the Western Cape region:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Comprehensive painting solutions for all property types throughout the Western Cape.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">All Western Cape regions</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7AM-6PM</p>
                    <p className="text-gray-600">Sat: 8AM-3PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>•</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
